import { FC, PropsWithChildren, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Exercise } from "types";
import ExerciseHelpers from "helpers/ExerciseHelpers";
import { dashboardVelocityOneRepMaxEnabled } from "config/features";
import { firstBy } from "thenby";
import useExercises from "providers/ExercisesProvider/useExercises";
import ExercisesContext from "providers/ExercisesProvider/ExercisesContext";
import MetadataProvider from "providers/MetadataProvider";

export const ExercisesWithOneRepMaxMetricProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const exercises = useExercises();

  /**
   * This is used to add the 1RM metrics to exercises that support them:
   * - lvpAutomatedOneRepMax (velocity-based) when the exercise is LVP capable and `dashboardVelocityOneRepMaxEnabled` is on
   * - weightOneRepMax (manual) when the oneRepMaxEnabled flag is set to true
   *
   * This feature is exclusive to dashboards because they use the
   * aggregated fetch apis.
   */
  const exerciseWithOneRepMaxMetric: Exercise[] = useMemo(
    () =>
      exercises.map((exercise: Exercise) => {
        const oneRepMaxMetrics = [
          dashboardVelocityOneRepMaxEnabled && ExerciseHelpers.isLvpCapableExercise(exercise)
            ? ExerciseHelpers.createOneRepMaxMetric({
                name: t("dashboard.exercise_metric_one_rep_max_velocity"),
                field: "lvpAutomatedOneRepMax",
              })
            : null,
          exercise.isOneRepMaxEnabled
            ? ExerciseHelpers.createOneRepMaxMetric({
                name: t("dashboard.exercise_metric_one_rep_max_manual"),
                field: "weightOneRepMax",
              })
            : null,
        ].filter((metric) => metric !== null);

        if (!oneRepMaxMetrics.length) {
          return exercise;
        }

        return {
          ...exercise,
          metrics: [...exercise.metrics, ...oneRepMaxMetrics].sort(firstBy("name")),
        };
      }),
    [exercises, t]
  );

  return (
    <ExercisesContext.Provider value={exerciseWithOneRepMaxMetric}>
      <MetadataProvider>{children}</MetadataProvider>
    </ExercisesContext.Provider>
  );
};
